import React from 'react';
import { ShortCode } from '@highereducation/wordpress';
import { Tabs } from '@highereducation/hen-ui';

type TabsItemShortcodeProps = {
  value?: string;
  label?: string;
  disabled?: string;
  children?: React.ReactNode;
  [key: string]: unknown;
};

type TabsShortcodeProps = {
  orientation?: 'horizontal' | 'vertical';
  'default-value'?: string;
  variant?: 'default' | 'accent';
  children?: React.ReactNode;
  [key: string]: unknown;
};

/**
 * TabsShortcode - Shortcode wrapper for Tabs composite component.
 * Use [hen-ui-tabs-item] as children for each tab panel.
 *
 * @example
 * [hen-ui-tabs default-value="tab1"]
 *   [hen-ui-tabs-item value="tab1" label="Overview"]Overview content here.[/hen-ui-tabs-item]
 *   [hen-ui-tabs-item value="tab2" label="Details"]Details content here.[/hen-ui-tabs-item]
 *   [hen-ui-tabs-item value="tab3" label="FAQ"]FAQ content here.[/hen-ui-tabs-item]
 * [/hen-ui-tabs]
 *
 * @example Accent active-indicator (primary-700 underline) for brand section tabs:
 * [hen-ui-tabs variant="accent" default-value="student"]
 *   [hen-ui-tabs-item value="student" label="Student Resources"]...[/hen-ui-tabs-item]
 * [/hen-ui-tabs]
 */
export const TabsShortcode: ShortCode<TabsShortcodeProps> = {
  component: ({
    orientation = 'horizontal',
    'default-value': defaultValue,
    variant = 'default',
    children,
  }) => {
    const tabs = React.Children.toArray(children)
      .filter((child): child is React.ReactElement<TabsItemShortcodeProps> =>
        React.isValidElement(child),
      )
      .map((child) => ({
        value: child.props.value ?? '',
        label: child.props.label ?? '',
        disabled: child.props.disabled === 'true',
        content: child.props.children,
      }));

    if (tabs.length === 0) return null;

    return (
      <Tabs
        orientation={orientation}
        defaultValue={defaultValue ?? tabs[0]?.value}
        variant={variant}
        tabs={tabs}
      />
    );
  },
};

export const TabsItemShortcode: ShortCode<TabsItemShortcodeProps> = {
  component: () => null,
};
